import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/StatusTracker";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PlusCircle, Briefcase, Store } from "lucide-react";

export default function ClientDashboard() {
  const { user, setRole } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);

  useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);

  const becomeBusiness = async () => {
    try { await setRole("business"); toast.success("Business account created. Complete your profile."); navigate("/business"); }
    catch { toast.error("Could not switch role"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-950">Welcome, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-1 text-slate-500">Track your jobs, quotes, and escrow.</p>
        </div>
        <Button data-testid="dashboard-post-job-button" onClick={() => navigate("/post-job")}
          className="bg-slate-950 hover:bg-amber-600 text-white rounded-full">
          <PlusCircle size={16} className="mr-1" /> Post a job
        </Button>
      </div>

      <div className="mt-8">
        <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><Briefcase size={18} /> Your jobs</h2>
        {jobs.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">
            <p>No jobs yet. Post your first job to start receiving quotes.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {jobs.map((j) => (
              <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`job-row-${j.id}`}
                className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 hover:border-amber-400 hover:shadow-md transition-all">
                <div>
                  <h3 className="font-semibold text-slate-900">{j.title}</h3>
                  <p className="text-sm text-slate-500">{j.trade} · {j.city}</p>
                </div>
                <StatusPill status={j.status} />
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 rounded-2xl bg-slate-950 text-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Store size={22} className="text-amber-500" />
          <div>
            <h3 className="font-heading font-bold">Own a Black-owned business?</h3>
            <p className="text-sm text-slate-400">Get vetted and start receiving jobs in your area.</p>
          </div>
        </div>
        <Button data-testid="become-business-button" onClick={becomeBusiness}
          className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-semibold">
          Join as a business
        </Button>
      </div>
    </div>
  );
}
